import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchModuleAccess, updateModuleAccess } from '../api';

/**
 * Partner Controls — Module Access.
 *
 * Per-firm, per-junior-role overrides of a module's default access. Each row is
 * a toggle: ON = the role keeps its normal (default) access; OFF = the role
 * loses all access to that module across the app AND the API for this firm.
 * With every toggle ON (the default, no override rows) the platform behaves
 * exactly as it did before this screen existed.
 *
 * Backend: GET/PATCH firm/module-access/ (FirmModuleAccessView), partners +
 * superusers only. See legal_ops/module_access.py.
 */
function ModuleAccessControls({ authAxios, showFormMessage, userRole }) {
  const canManage = useMemo(
    () => ['PARTNER', 'SUPERUSER'].includes(userRole),
    [userRole],
  );

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // `${moduleKey}:${role}` mid-save

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await fetchModuleAccess(authAxios));
    } catch {
      showFormMessage('error', 'Failed to load module access settings.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  const toggle = useCallback(async (moduleKey, moduleLabel, role, roleLabel, nextDisabled) => {
    const key = `${moduleKey}:${role}`;
    setSavingKey(key);
    try {
      const updated = await updateModuleAccess(authAxios, {
        module_key: moduleKey, role, disabled: nextDisabled,
      });
      setData(updated);
      showFormMessage(
        'success',
        nextDisabled
          ? `${moduleLabel} turned off for ${roleLabel}.`
          : `${moduleLabel} restored to default for ${roleLabel}.`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showFormMessage('error', detail || 'Failed to update module access.');
    } finally {
      setSavingKey(null);
    }
  }, [authAxios, showFormMessage]);

  if (!canManage) {
    return (
      <div className="p-8 text-center text-gray-600">
        Only partners can manage module access.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Module Access</h1>
        <p className="text-sm text-gray-600 mt-1">
          Control which parts of the app your junior staff can reach — for this
          firm only. Leaving a toggle <strong>on</strong> keeps that role's
          normal access. Turning it <strong>off</strong> removes the role's
          access to that module everywhere, including direct API calls.
        </p>
      </div>

      {isLoading && <div className="text-gray-500">Loading…</div>}

      {!isLoading && data && data.enforced === false && (
        <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-sm text-amber-800">
          Module access enforcement is currently switched off system-wide
          (<code>LEGALOPS_MODULE_ACCESS_ENFORCED=0</code>). Changes here are
          saved but have no effect until it is re-enabled.
        </div>
      )}

      {!isLoading && data && data.modules.map((module) => (
        <div key={module.key} className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800">{module.label}</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">{module.description}</p>

          <div className="divide-y divide-gray-100">
            {module.roles.map((row) => {
              const key = `${module.key}:${row.role}`;
              const enabled = !row.disabled;
              const busy = savingKey === key;
              return (
                <div key={row.role} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-gray-800">{row.role_label}</div>
                    <div className="text-xs text-gray-500">
                      Default: {row.default_label}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`${module.label} access for ${row.role_label}`}
                    disabled={busy}
                    onClick={() => toggle(
                      module.key, module.label, row.role, row.role_label, enabled,
                    )}
                    className={[
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                      enabled ? 'bg-green-500' : 'bg-gray-300',
                      busy ? 'opacity-50 cursor-wait' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                        enabled ? 'translate-x-6' : 'translate-x-1',
                      ].join(' ')}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ModuleAccessControls;
